// src/components/Header.js
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../LanguageContext.js';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const { language, t, toggleLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const headerOffset = 80;
      const elementPosition = section.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    
    setMobileMenuOpen(false);
  };

  const logoHeight = isScrolled ? 'h-7' : 'h-9';

  return (
    <header 
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'shadow-sm py-2 md:py-3' : 'py-4 md:py-5'
      }`}
      style={{
        backgroundColor: '#FFFFFF',
        borderBottom: isScrolled ? '1px solid #E5E5E5' : 'none'
      }}
    >
      <div className="container mx-auto px-4 md:px-6 flex justify-between items-center">
        {/* Logo */}
        <button 
          onClick={() => scrollToSection('home')}
          className="flex items-center flex-shrink-0"
        >
          <img
            src="/images/DHOME_Logo.webp"
            alt="DHOME"
            className={`object-contain transition-all duration-300 ${logoHeight}`}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'block';
            }}
          />
          <span className="hidden font-black text-xl md:text-2xl" style={{ color: '#000000' }}>DHOME</span>
        </button>
        
        {/* Desktop: Lingua + CTA */}
        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          {/* Language switcher */}
          <div className="flex gap-3 text-xs font-medium uppercase tracking-wider">
            <button 
              onClick={language === 'en' ? toggleLanguage : undefined}
              className="transition-all duration-300"
              style={{
                color: language === 'it' ? '#000000' : '#585856'
              }}
              onMouseEnter={(e) => language !== 'it' && (e.target.style.color = '#30302e')}
              onMouseLeave={(e) => e.target.style.color = language === 'it' ? '#000000' : '#585856'}
            >
              IT
            </button>
            <span style={{ color: '#B0B0B0' }}>/</span>
            <button 
              onClick={language === 'it' ? toggleLanguage : undefined}
              className="transition-all duration-300"
              style={{
                color: language === 'en' ? '#000000' : '#585856'
              }}
              onMouseEnter={(e) => language !== 'en' && (e.target.style.color = '#30302e')}
              onMouseLeave={(e) => e.target.style.color = language === 'en' ? '#000000' : '#585856'}
            >
              EN
            </button>
          </div>

          {/* CTA Button */}
          <button 
            onClick={() => scrollToSection('contact')}
            className="px-6 py-2.5 font-semibold text-xs uppercase tracking-wider transition-all duration-300"
            style={{
              backgroundColor: '#000000',
              color: '#FFFFFF'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#30302e'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#000000'}
          >
            {t('nav', 'sell_property')}
          </button>
        </div>
        
        {/* Mobile Menu Button */}
        <div className="flex items-center md:hidden gap-3">
          {/* Mobile language switcher */}
          <div className="flex gap-1.5 text-xs font-medium uppercase">
            <button 
              onClick={language === 'en' ? toggleLanguage : undefined}
              className="transition-all duration-300"
              style={{
                color: language === 'it' ? '#000000' : '#585856'
              }}
              onMouseEnter={(e) => language !== 'it' && (e.target.style.color = '#30302e')}
              onMouseLeave={(e) => e.target.style.color = language === 'it' ? '#000000' : '#585856'}
            >
              IT
            </button>
            <span style={{ color: '#B0B0B0' }}>/</span>
            <button 
              onClick={language === 'it' ? toggleLanguage : undefined}
              className="transition-all duration-300"
              style={{
                color: language === 'en' ? '#000000' : '#585856'
              }}
              onMouseEnter={(e) => language !== 'en' && (e.target.style.color = '#30302e')}
              onMouseLeave={(e) => e.target.style.color = language === 'en' ? '#000000' : '#585856'}
            >
              EN
            </button>
          </div>
          
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{ color: '#000000' }}
            className="p-1"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden" style={{ backgroundColor: '#FFFFFF', borderTop: '1px solid #E5E5E5' }}>
          <div className="container mx-auto px-4 py-4 flex flex-col gap-3">
            <button 
              onClick={() => scrollToSection('contact')}
              className="w-full py-3 px-4 font-semibold text-xs uppercase transition-colors duration-300 text-center"
              style={{ backgroundColor: '#000000', color: '#FFFFFF' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#30302e'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#000000'}
            >
              {t('nav', 'sell_property')}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;